<template>
  <b-modal
    v-model="showModal"
    title="Calculator"
    size="sm"
    hide-footer
    modal-class="calculator-modal-bottom-right"
    hide-backdrop
    @hidden="clearCalculator"
  >
    <div class="calculator">
      <div class="display">
        <div
          v-if="lastExpression"
          class="last-calculation"
        >
          {{ lastExpression }}
        </div>
        <div class="current-value">
          {{ display || '0' }}
        </div>
      </div>
      <div class="keypad">
        <button
          v-for="btn in buttons"
          :key="btn.value"
          :class="['btn-calc', btn.class]"
          @click="handleButtonClick(btn.value)"
        >
          {{ btn.label }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal } from 'bootstrap-vue'

export default {
  components: {
    BModal,
  },
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      display: '',
      expression: '',
      lastExpression: '',
      justCalculated: false,
      buttons: [
        { label: 'C', value: 'clear', class: 'btn-clear' },
        { label: '←', value: 'backspace', class: 'btn-backspace' },
        { label: '/', value: '/', class: 'btn-operator' },
        { label: '7', value: '7', class: 'btn-number' },
        { label: '8', value: '8', class: 'btn-number' },
        { label: '9', value: '9', class: 'btn-number' },
        { label: '*', value: '*', class: 'btn-operator' },
        { label: '4', value: '4', class: 'btn-number' },
        { label: '5', value: '5', class: 'btn-number' },
        { label: '6', value: '6', class: 'btn-number' },
        { label: '-', value: '-', class: 'btn-operator' },
        { label: '1', value: '1', class: 'btn-number' },
        { label: '2', value: '2', class: 'btn-number' },
        { label: '3', value: '3', class: 'btn-number' },
        { label: '+', value: '+', class: 'btn-operator' },
        { label: '0', value: '0', class: 'btn-zero' },
        { label: '.', value: '.', class: 'btn-number' },
        { label: '=', value: '=', class: 'btn-equals' },
      ],
    }
  },
  computed: {
    showModal: {
      get() {
        return this.value
      },
      set(newVal) {
        this.$emit('input', newVal)
      },
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyPress)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyPress)
  },
  methods: {
    handleKeyPress(e) {
      if (!this.showModal) return

      if (e.key >= '0' && e.key <= '9') {
        this.appendNumber(e.key)
      } else if (['+', '-', '*', '/'].includes(e.key)) {
        this.setOperator(e.key)
      } else if (e.key === '.') {
        this.appendNumber('.')
      } else if (e.key === 'Enter' || e.key === '=') {
        e.preventDefault()
        this.calculate()
      } else if (e.key === 'Backspace') {
        e.preventDefault()
        this.backspace()
      } else if (e.key === 'Escape' || e.key.toLowerCase() === 'c') {
        if (e.key === 'Escape') {
          this.showModal = false
        } else {
          this.clear()
        }
      }
    },
    handleButtonClick(value) {
      if (value === 'clear') {
        this.clear()
      } else if (value === 'backspace') {
        this.backspace()
      } else if (value === '=') {
        this.calculate()
      } else if (['+', '-', '*', '/'].includes(value)) {
        this.setOperator(value)
      } else {
        this.appendNumber(value)
      }
    },
    appendNumber(num) {
      // If just calculated, start fresh with new number
      if (this.justCalculated) {
        this.expression = num
        this.justCalculated = false
      } else {
        // Prevent multiple decimal points in the same number
        const parts = this.expression.split(/[+\-*/]/)
        const lastPart = parts[parts.length - 1]
        if (num === '.' && lastPart.includes('.')) return

        this.expression += num
      }
      this.display = this.expression
    },
    setOperator(op) {
      // If just calculated, continue with result
      if (this.justCalculated) {
        this.justCalculated = false
      }

      // Don't add operator if expression is empty
      if (this.expression === '') return

      // Don't add operator if last character is already an operator
      const lastChar = this.expression.slice(-1)
      if (['+', '-', '*', '/'].includes(lastChar)) {
        // Replace last operator
        this.expression = this.expression.slice(0, -1) + op
      } else {
        this.expression += op
      }

      this.display = this.expression
    },
    evaluateExpression(expr) {
      // Parse and evaluate mathematical expression safely
      // Remove leading zeros from numbers
      const sanitized = expr.replace(/\b0+(\d+)/g, '$1')

      // Split by operators while keeping them
      const tokens = sanitized.split(/([+\-*/])/).filter(t => t.trim() !== '')

      if (tokens.length === 0) return 0

      // Convert to numbers and operators
      const values = []
      const operators = []

      for (let i = 0; i < tokens.length; i += 1) {
        const token = tokens[i]
        if (['+', '-', '*', '/'].includes(token)) {
          operators.push(token)
        } else {
          values.push(parseFloat(token))
        }
      }

      // Handle multiplication and division first
      for (let i = 0; i < operators.length;) {
        if (operators[i] === '*' || operators[i] === '/') {
          const left = values[i]
          const right = values[i + 1]
          let result

          if (operators[i] === '*') {
            result = left * right
          } else {
            if (right === 0) throw new Error('Division by zero')
            result = left / right
          }

          values.splice(i, 2, result)
          operators.splice(i, 1)
        } else {
          i += 1
        }
      }

      // Handle addition and subtraction
      let result = values[0]
      for (let i = 0; i < operators.length; i += 1) {
        if (operators[i] === '+') {
          result += values[i + 1]
        } else if (operators[i] === '-') {
          result -= values[i + 1]
        }
      }

      return result
    },
    calculate() {
      if (this.expression === '') return

      try {
        const result = this.evaluateExpression(this.expression)

        this.lastExpression = `${this.expression} = ${result}`
        this.expression = result.toString()
        this.display = result.toString()
        this.justCalculated = true
      } catch (error) {
        this.display = 'Error'
        this.expression = ''
      }
    },
    clear() {
      this.expression = ''
      this.display = ''
      this.lastExpression = ''
      this.justCalculated = false
    },
    backspace() {
      if (this.expression) {
        this.expression = this.expression.slice(0, -1)
        this.display = this.expression || '0'
      }
    },
    clearCalculator() {
      this.clear()
    },
  },
}
</script>

<style scoped>
.calculator {
  padding: 8px;
}

.display {
  background-color: #2d3748;
  color: #fff;
  text-align: right;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 8px;
  min-height: 50px;
}

.last-calculation {
  font-size: 11px;
  color: #a0aec0;
  margin-bottom: 4px;
  min-height: 14px;
}

.current-value {
  font-size: 18px;
  font-weight: bold;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.keypad {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

.btn-calc {
  padding: 10px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-calc:hover {
  transform: scale(1.05);
}

.btn-calc:active {
  transform: scale(0.95);
}

.btn-number {
  background-color: #f7fafc;
  color: #2d3748;
}

.btn-number:hover {
  background-color: #edf2f7;
}

.btn-operator {
  background-color: #4299e1;
  color: #fff;
}

.btn-operator:hover {
  background-color: #3182ce;
}

.btn-clear {
  background-color: #fc8181;
  color: #fff;
  grid-column: span 2;
}

.btn-clear:hover {
  background-color: #f56565;
}

.btn-backspace {
  background-color: #ed8936;
  color: #fff;
}

.btn-backspace:hover {
  background-color: #dd6b20;
}

.btn-equals {
  background-color: #48bb78;
  color: #fff;
  grid-row: span 2;
}

.btn-equals:hover {
  background-color: #38a169;
}

.btn-zero {
  background-color: #f7fafc;
  color: #2d3748;
  grid-column: span 2;
}

.btn-zero:hover {
  background-color: #edf2f7;
}
</style>

<style>
/* Global styles for positioning modal at bottom right */
.calculator-modal-bottom-right .modal-dialog {
  position: fixed;
  bottom: 20px;
  right: 20px;
  margin: 0;
  width: 300px;
  max-width: 300px;
}

.calculator-modal-bottom-right .modal-content {
  border-radius: 8px;
  box-shadow: none;
}
</style>
